import { useEffect, useState } from 'react';
import NavigationMenu from '../../components/NavigationMenu/NavigationMenu';
import { listTrash, permanentlyDeleteTrash, restoreTrash } from '../../../services/TrashService';
import './TrashBin.css';

const displayDeletedDate = (deletedAt) => {
    if (!deletedAt) return '—';
    const date = new Date(deletedAt);
    return Number.isNaN(date.getTime()) ? deletedAt : date.toLocaleDateString();
};

const TrashBin = () => {
    const [trashItems, setTrashItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [processingUuid, setProcessingUuid] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, item: null });

    const loadTrash = async () => {
        setError('');
        try {
            setTrashItems(await listTrash());
        } catch (loadError) {
            setError(loadError instanceof TypeError ? 'Unable to connect to the trash service.' : loadError.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadTrash();
    }, []);

    const handleRestore = async (item) => {
        setError('');
        setProcessingUuid(item.uuid);
        try {
            await restoreTrash(item.uuid);
            await loadTrash();
        } catch (restoreError) {
            setError(restoreError instanceof TypeError ? 'Unable to connect to the trash service.' : restoreError.message);
        } finally {
            setProcessingUuid(null);
        }
    };

    const handlePermanentDelete = (item) => {
        setError('');
        setDeleteConfirm({ show: true, item });
    };

    const confirmPermanentDelete = async () => {
        if (!deleteConfirm.item) return;
        const item = deleteConfirm.item;
        setError('');
        setProcessingUuid(item.uuid);
        try {
            await permanentlyDeleteTrash(item.uuid);
            setDeleteConfirm({ show: false, item: null });
            await loadTrash();
        } catch (deleteError) {
            setError(deleteError instanceof TypeError ? 'Unable to connect to the trash service.' : deleteError.message);
        } finally {
            setProcessingUuid(null);
        }
    };

    const cancelPermanentDelete = () => {
        if (processingUuid) return;
        setDeleteConfirm({ show: false, item: null });
    };

    return (
        <div className="trash-bin">
            <NavigationMenu />
            <div className="trash-content">
                <header className="trash-header">
                    <h1 className="trash-title">Trash Bin</h1>
                    <p className="trash-subtitle">Restore or permanently delete items</p>
                </header>
                <main className="trash-main">
                    {isLoading ? (
                        <div className="trash-feedback">Loading trash...</div>
                    ) : error && trashItems.length === 0 ? (
                        <div className="trash-feedback trash-error" role="alert">{error}</div>
                    ) : trashItems.length === 0 ? (
                        <div className="empty-trash">
                            <i className="fa-solid fa-trash-can empty-icon"></i>
                            <p className="empty-text">Trash bin is empty</p>
                            <p className="empty-subtext">Deleted items will appear here</p>
                        </div>
                    ) : (
                        <>
                            {error && <div className="trash-feedback trash-error" role="alert">{error}</div>}
                            <div className="table-container">
                                <div className="table-scroll">
                                    <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Name</th>
                                            <th>Deleted On</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trashItems.map(item => (
                                            <tr key={item.uuid}>
                                                <td>{item.type || item.resource}</td>
                                                <td>{item.name || item.entityUuid}</td>
                                                <td>{displayDeletedDate(item.deletedAt)}</td>
                                                <td>
                                                    <div className="trash-table-actions">
                                                        <button className="action-btn restore" onClick={() => handleRestore(item)} disabled={processingUuid === item.uuid}>
                                                            <i className="fa-solid fa-rotate-left"></i> {processingUuid === item.uuid ? 'Restoring...' : 'Restore'}
                                                        </button>
                                                        <button className="action-btn delete-permanent" onClick={() => handlePermanentDelete(item)} disabled={processingUuid === item.uuid}>
                                                            <i className="fa-solid fa-trash"></i> Delete Permanently
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </main>
            </div>

            {deleteConfirm.show && (
                <div className="modal-overlay">
                    <div className="modal">
                        <div className="modal-header">
                            <h3 className="modal-title">Permanently Delete</h3>
                            <button className="modal-close" onClick={cancelPermanentDelete} disabled={Boolean(processingUuid)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <p><strong>Warning: This action cannot be undone!</strong></p>
                                <p>Are you sure you want to permanently remove "{deleteConfirm.item.name || deleteConfirm.item.entityUuid}" from the app?</p>
                                <p className="trash-notice"><i className="fa-solid fa-exclamation-triangle"></i> Only the local app records are removed. The connected OAuth account and Stripe customer are not deleted.</p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-button cancel" onClick={cancelPermanentDelete} disabled={Boolean(processingUuid)}>
                                Cancel
                            </button>
                            <button className="modal-button delete" onClick={confirmPermanentDelete} disabled={Boolean(processingUuid)} style={{ background: '#d1242f' }}>
                                {processingUuid ? 'Deleting...' : 'Delete Permanently'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrashBin;
